import type { RegionBorders, Zone } from '@tsagko/label-contracts'
import { computeZoneLayout, type TemplateDims } from './layout'

type Edge = keyof RegionBorders // 'top' | 'right' | 'bottom' | 'left'

const EPSILON_MM = 1e-6
const OPPOSITE: Record<Edge, Edge> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }

interface EdgeSegment {
  axis: 'h' | 'v'
  pos: number // the fixed coordinate of the line
  start: number // start along the perpendicular axis
  length: number
}

function edgeSegment(box: { x: number; y: number; w: number; h: number }, edge: Edge): EdgeSegment {
  switch (edge) {
    case 'top':
      return { axis: 'h', pos: box.y, start: box.x, length: box.w }
    case 'bottom':
      return { axis: 'h', pos: box.y + box.h, start: box.x, length: box.w }
    case 'left':
      return { axis: 'v', pos: box.x, start: box.y, length: box.h }
    case 'right':
      return { axis: 'v', pos: box.x + box.w, start: box.y, length: box.h }
  }
}

function near(a: number, b: number): boolean {
  return Math.abs(a - b) < EPSILON_MM
}

function sameSeg(a: EdgeSegment, b: EdgeSegment): boolean {
  return (
    a.axis === b.axis &&
    near(a.pos, b.pos) &&
    near(a.start, b.start) &&
    near(a.length, b.length)
  )
}

/**
 * Find another template-level leaf whose opposite edge is an EXACT
 * full-segment match for the selected leaf's `edge`. This is the partner the
 * coincidence-linking write-through updates. Partial overlaps return undefined
 * (they rely on render-time de-dup, not linking). Baked-instance interior
 * leaves (bakedDepth > 0) are never linkable.
 */
export function findCoincidentEdge(
  layout: Zone,
  dims: TemplateDims,
  path: string[],
  edge: Edge
): { path: string[]; edge: Edge } | undefined {
  const zones = computeZoneLayout(layout, dims)
  const selfKey = path.join('/')
  const self = zones.find(z => z.path.join('/') === selfKey)
  if (!self) return undefined
  const selfSeg = edgeSegment({ x: self.x, y: self.y, w: self.w, h: self.h }, edge)
  const want = OPPOSITE[edge]
  for (const z of zones) {
    if (z.path.join('/') === selfKey || z.bakedDepth !== 0) continue
    if (sameSeg(selfSeg, edgeSegment({ x: z.x, y: z.y, w: z.w, h: z.h }, want))) {
      return { path: z.path, edge: want }
    }
  }
  return undefined
}
